import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  getLocale,
  setLocale,
  subscribeLocale,
  t,
  type LocaleCode,
  type Translation,
} from './i18n';
import { net, type AppState } from './net';
import { getTheme, savedThemeId, saveThemeId, subscribeThemes } from './theme/registry';
import type { CardTheme } from './theme/types';

export function useAppState(): AppState {
  return useSyncExternalStore(net.subscribe, net.getSnapshot, net.getSnapshot);
}

/** The active translation catalogue. Re-renders when the language changes. */
export function useT(): Translation {
  return useSyncExternalStore(subscribeLocale, t, t);
}

/** Current language code plus a setter, for the picker. */
export function useLocale(): [LocaleCode, (code: LocaleCode) => void] {
  const code = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  return [code, setLocale];
}

/** Current card theme + setter, persisted to localStorage. */
export function useCardTheme(): [CardTheme, (id: string) => void] {
  const [id, setId] = useState(savedThemeId);
  const [, bump] = useState(0);

  // The theme index loads asynchronously; re-render when it arrives.
  useEffect(() => subscribeThemes(() => bump((n) => n + 1)), []);

  const theme = useMemo(() => getTheme(id), [id]);
  const select = (next: string) => {
    saveThemeId(next);
    setId(next);
  };
  return [theme, select];
}

/** Seconds remaining until `deadline`, ticking every 250ms. Null when idle. */
export function useCountdown(deadline: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [deadline]);

  if (deadline === null) return null;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** Fires `onChange` whenever `value` transitions to a new truthy value. */
export function useOnChange<T>(value: T, onChange: (value: T, previous: T | undefined) => void): void {
  const previous = useRef<T | undefined>(undefined);
  useEffect(() => {
    if (previous.current !== value) {
      onChange(value, previous.current);
      previous.current = value;
    }
  }, [value, onChange]);
}

/** True for `ms` after `trigger` changes — handy for one-shot animations. */
export function useFlash(trigger: unknown, ms = 700): boolean {
  const [on, setOn] = useState(false);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setOn(true);
    const id = window.setTimeout(() => setOn(false), ms);
    return () => window.clearTimeout(id);
  }, [trigger, ms]);

  return on;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}
